import {
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactElement,
  type ReactNode,
} from 'react';
import { Tooltip } from 'radix-ui';
import {
  Circle,
  CircleCheck,
  CircleSlash,
  FlaskConical,
  OctagonAlert,
  Radio,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/cn';

export function IconButton({
  label,
  active,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex h-8 w-8 items-center justify-center rounded-control text-ink-2 transition hover:bg-overlay hover:text-ink disabled:opacity-40',
        active && 'bg-accent-soft text-accent',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function TextButton({
  active,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      className={cn(
        'rounded-control px-2.5 py-1 text-xs font-medium text-ink-2 transition hover:bg-overlay hover:text-ink disabled:opacity-40',
        active && 'bg-accent-soft text-accent',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export type Tone = 'nominal' | 'caution' | 'warning' | 'offline' | 'live' | 'sim' | 'neutral';

const toneClass: Record<Tone, string> = {
  nominal: 'bg-nominal/15 text-nominal border-nominal/30',
  caution: 'bg-caution/15 text-caution border-caution/30',
  warning: 'bg-warning/15 text-warning border-warning/30',
  offline: 'bg-offline/15 text-offline border-offline/30',
  live: 'bg-live/15 text-live border-live/30',
  sim: 'bg-sim/15 text-sim border-sim/30',
  neutral: 'bg-overlay text-ink-2 border-hairline-strong',
};

/** Status is dual-encoded: colour plus the label text; add an icon where it matters. */
export function Chip({ tone = 'neutral', children, className, pulse }: { tone?: Tone; children: ReactNode; className?: string; pulse?: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium tracking-wide', toneClass[tone], className)}>
      {pulse && <span aria-hidden className="relative flex h-1.5 w-1.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" /><span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" /></span>}
      {children}
    </span>
  );
}

const STATUS_ICON: Record<Tone, LucideIcon> = {
  nominal: CircleCheck,
  caution: TriangleAlert,
  warning: OctagonAlert,
  offline: CircleSlash,
  live: Radio,
  sim: FlaskConical,
  neutral: Circle,
};

const STATUS_TEXT: Record<Tone, string> = {
  nominal: 'text-nominal',
  caution: 'text-caution',
  warning: 'text-warning',
  offline: 'text-offline',
  live: 'text-live',
  sim: 'text-sim',
  neutral: 'text-ink-3',
};

/**
 * The shape half of a dual-encoded status: a distinct icon per tone (check,
 * triangle, octagon, slash) in the tone's colour, with the status word for
 * screen readers. Put it next to a value whose colour carries the status.
 */
export function StatusIcon({ tone, label, className }: { tone: Tone; label: string; className?: string }) {
  const Icon = STATUS_ICON[tone];
  return (
    <>
      <Icon aria-hidden className={cn('h-3 w-3 shrink-0', STATUS_TEXT[tone], className)} />
      <span className="sr-only">{label}</span>
    </>
  );
}

export function PanelHeader({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex h-9 shrink-0 items-center justify-between gap-2 border-b border-hairline px-3">
      <div className="min-w-0 truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-2">{title}</div>
      <div className="flex shrink-0 items-center gap-1">{children}</div>
    </div>
  );
}

export function Placeholder({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="flex flex-col gap-2 p-panel text-sm">
      <div className="font-medium text-ink">{title}</div>
      <ul className="list-disc space-y-1 pl-5 text-ink-3">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * A tooltip that also opens on keyboard focus (a title attribute does not).
 * Each instance brings its own provider, so it works anywhere in the tree.
 * `disabled` renders the child alone, for when a visible label says the same.
 */
export function Tip({
  label,
  children,
  side = 'bottom',
  disabled,
}: {
  label: ReactNode;
  children: ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  disabled?: boolean;
}) {
  if (disabled) return children;
  return (
    <Tooltip.Provider delayDuration={300} skipDelayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            side={side}
            sideOffset={6}
            collisionPadding={8}
            className="z-50 rounded-control border border-hairline-strong bg-overlay px-2 py-1 text-[11px] font-medium text-ink shadow-elev-2"
          >
            {label}
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

const FADE_PX = 24;

/**
 * A row that scrolls sideways when its items do not fit, with fades on the
 * edges that have more to show (data-overflow-start and data-overflow-end
 * mirror them). Keyboard focus scrolls items clear of the fades; a vertical
 * wheel scrolls the row; the item marked data-active="true" (or a Radix
 * trigger with data-state="active") is scrolled into view whenever
 * `activeKey` changes.
 */
export function ScrollFade({
  children,
  className,
  innerClassName,
  activeKey,
  label,
  role,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  activeKey?: string;
  label?: string;
  role?: string;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  useLayoutEffect(() => {
    const el = scroller.current;
    const inner = content.current;
    if (!el || !inner) return;
    const update = () => {
      const start = el.scrollLeft > 1;
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    observer.observe(inner);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  // Scroll only this row (offsetLeft within the positioned content), never an ancestor.
  useLayoutEffect(() => {
    const el = scroller.current;
    const item = content.current?.querySelector<HTMLElement>('[data-active="true"], [role="tab"][data-state="active"]');
    if (!el || !item) return;
    const left = item.offsetLeft - FADE_PX;
    const right = item.offsetLeft + item.offsetWidth + FADE_PX;
    if (left < el.scrollLeft) el.scrollLeft = Math.max(0, left);
    else if (right > el.scrollLeft + el.clientWidth) el.scrollLeft = right - el.clientWidth;
  }, [activeKey]);

  const masked = edges.start || edges.end;
  const mask = `linear-gradient(to right, transparent 0, #000 ${edges.start ? FADE_PX : 0}px, #000 calc(100% - ${edges.end ? FADE_PX : 0}px), transparent 100%)`;
  return (
    <div
      ref={scroller}
      data-overflow-start={edges.start ? '' : undefined}
      data-overflow-end={edges.end ? '' : undefined}
      onWheel={(e) => {
        const el = scroller.current;
        if (el && el.scrollWidth > el.clientWidth && Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft += e.deltaY;
      }}
      className={cn('min-w-0 overflow-x-auto overflow-y-hidden scrollbar-none [contain:inline-size] [scroll-padding-inline:24px]', className)}
      style={masked ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
    >
      <div ref={content} role={role} aria-label={label} className={cn('relative flex w-max items-center', innerClassName)}>
        {children}
      </div>
    </div>
  );
}
